// Shared WonderLabs / teacher speech player. Standard Ask AI still streams
// audio_chunk URLs from /api/ask_ai; completed, inquiry-guarded answers go to
// /api/wonderlabs/tts only AFTER the client has approved the final text.
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { FASTAPI_BASE_URL } from "../../../config";

// Short silent WAV played from a real tap, before an asynchronous TTS request.
// This primes the *same* HTMLAudioElement so playback still has user activation
// in browsers that block a first play() after the network request resolves.
const USER_GESTURE_SILENCE = "data:audio/wav;base64,UklGRkQDAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YSADAACAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgA==";

export default function useOpenAiChunkedSpeech({ userId = "Guest", language = "en" } = {}) {
  const audioRef = useRef(null);
  const queueRef = useRef([]);
  const controllerRef = useRef(null);
  const generationRef = useRef(0);
  const playingRef = useRef(false);
  const playRef = useRef(() => {});
  const [speaking, setSpeaking] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | preparing | playing
  const [error, setError] = useState("");

  const stop = useCallback(() => {
    generationRef.current += 1;
    controllerRef.current?.abort?.();
    controllerRef.current = null;
    queueRef.current = [];
    playingRef.current = false;
    setSpeaking(false);
    setPhase("idle");
    setError("");
    const audio = audioRef.current;
    if (audio) {
      audio.onended = null;
      audio.onerror = null;
      try { audio.pause(); audio.removeAttribute("src"); audio.load(); } catch (_error) {}
    }
  }, []);

  const primeFromGesture = useCallback(() => {
    if (typeof Audio === "undefined") return;
    try {
      if (!audioRef.current) audioRef.current = new Audio();
      const audio = audioRef.current;
      audio.onended = null;
      audio.onerror = null;
      audio.src = USER_GESTURE_SILENCE;
      // Start while still inside the original click handler. Never await here.
      const attempt = audio.play();
      attempt?.catch?.((failure) => {
        console.debug("Audio gesture priming unavailable", failure?.name || failure);
      });
    } catch (failure) {
      console.debug("Audio gesture priming unavailable", failure?.message || failure);
    }
  }, []);

  playRef.current = () => {
    if (playingRef.current || !queueRef.current.length) return;
    if (typeof Audio === "undefined") return;
    if (!audioRef.current) audioRef.current = new Audio();
    const audio = audioRef.current;
    const item = queueRef.current.shift();
    const generation = generationRef.current;
    playingRef.current = true;
    setSpeaking(true);
    setPhase("playing");
    const advance = (failed = false, failure = null) => {
      if (generation !== generationRef.current) return;
      playingRef.current = false;
      try {
        if (failed) item.onError?.(failure);
        else item.onEnd?.();
      } catch (_ignored) {}
      if (failed) {
        queueRef.current = [];
        setError(failure?.name === "NotAllowedError"
          ? "Your browser blocked audio playback. Allow sound and try again."
          : "Audio could not play. Check your sound settings and try again.");
      }
      if (!queueRef.current.length) {
        setSpeaking(false);
        setPhase("idle");
      } else playRef.current();
    };
    audio.onended = () => advance(false);
    audio.onerror = () => advance(true, new Error("Audio resource failed to load"));
    audio.src = item.url;
    try {
      const promise = audio.play();
      promise?.catch?.((failure) => advance(true, failure));
    } catch (failure) { advance(true, failure); }
  };

  const enqueue = useCallback((url, callbacks = {}) => {
    if (!url) return;
    queueRef.current.push({ url, ...callbacks });
    playRef.current();
  }, []);

  const speakText = useCallback(async (text, callbacks = {}) => {
    const cleaned = String(text || "").trim();
    if (!cleaned) return;
    stop();
    // Manual teacher playback originates in a real click; unlock audio before
    // axios awaits the TTS response. Automatic Swan speech never primes.
    if (callbacks.fromUserGesture === true) primeFromGesture();
    const generation = generationRef.current;
    const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    controllerRef.current = controller;
    setSpeaking(true);
    setPhase("preparing");
    try {
      const { data } = await axios.post(`${FASTAPI_BASE_URL}/wonderlabs/tts`, {
        user_id: userId,
        language,
        text: cleaned,
      }, { signal: controller?.signal });
      if (generation !== generationRef.current || controller?.signal?.aborted) return;
      controllerRef.current = null;
      if (data?.url) enqueue(data.url, callbacks);
      else {
        setSpeaking(false);
        setPhase("idle");
        setError("Speech is temporarily unavailable. Please try again.");
        callbacks.onError?.();
      }
    } catch (error) {
      if (generation !== generationRef.current) return;
      controllerRef.current = null;
      setSpeaking(false);
      setPhase("idle");
      if (!controller?.signal?.aborted) {
        setError(error?.response?.status === 422
          ? "This answer is too long for speech. Please shorten it and try again."
          : "Could not prepare speech. Please try again.");
        console.warn("OpenAI TTS unavailable; text remains visible", error?.response?.status || error?.message || error);
        callbacks.onError?.(error);
      }
    }
  }, [enqueue, language, primeFromGesture, stop, userId]);

  const speak = useCallback((text, options = {}) => speakText(text, options), [speakText]);
  useEffect(() => stop, [stop]);
  return {
    enqueue, stop, speakText, speak, speaking, phase, error,
    supported: typeof Audio !== "undefined",
  };
}
